'use server'

import { createClient, createServiceClient } from '@repo/db/server'
import { createClient as createPasswordClient } from '@supabase/supabase-js'
import { createHash, randomBytes } from 'node:crypto'
import { checkAal, passwordPolicyError } from '@repo/security/aal'
import { normalizePanelEmail, panelHesabiBul, panelHesabiKullanicidan } from '@repo/db/panel-hesap'
import { hasInvitationMailer, invitationRequestUrl, sendInvitationMail } from '@/lib/invitations-mail'

export async function updateEmail(newEmail: string, currentPassword?: string) {
  const supabase = await createClient()
  const { data: { user }, error: authError } = await supabase.auth.getUser()
  if (authError || !user) return { error: 'Oturum bulunamadı' }
  if (!(await checkAal(supabase, 'optional')).ok) return { error: 'İki faktörlü doğrulama gerekli.' }

  if (!currentPassword) return { error: 'Mevcut şifrenizi girin.' }
  if (!user.email) return { error: 'Hesabın e-posta adresi bulunamadı.' }
  let email: string
  try { email = normalizePanelEmail(newEmail) } catch { return { error: 'Geçerli bir e-posta adresi gerekli.' } }
  const admin = createServiceClient()
  const panelRpc = admin as unknown as { rpc: (name: string, args: Record<string, unknown>) => Promise<{ error: unknown }> }
  const account = await panelHesabiKullanicidan(admin, user.id)
  if (!account) return { error: 'Panel hesabı bulunamadı.' }
  if (email === account.email) {
    return { error: 'Yeni e-posta mevcut e-posta ile aynı.' }
  }

  if (!(await verifyPassword(user.id, user.email, currentPassword))) return { error: 'Mevcut şifre hatalı.' }
  if (!hasInvitationMailer()) return { error: 'E-posta gönderimi yapılandırılmamış. Adres değiştirilmedi.' }
  try {
    if (await panelHesabiBul(admin, account.tenantId, email)) return { error: 'Bu e-posta bu panelde zaten kayıtlı.' }
    const origin = await invitationRequestUrl('/')
    const token = randomBytes(32).toString('base64url')
    const tokenHash = createHash('sha256').update(token).digest('hex')
    const { error: pendingError } = await panelRpc.rpc('panel_email_change_request', {
      p_user_id: user.id, p_tenant_id: account.tenantId, p_email: email, p_token_hash: tokenHash,
    })
    if (pendingError) throw new Error('Doğrulama oluşturulamadı.')
    const confirmationUrl = new URL('/auth/confirm', origin)
    confirmationUrl.searchParams.set('type', 'panel_email_change')
    confirmationUrl.searchParams.set('uid', user.id)
    confirmationUrl.searchParams.set('token', token)
    await sendInvitationMail(email, 'Yeni e-posta adresinizi doğrulayın', `Yeni adresinizi doğrulamak için:\n${confirmationUrl}`)
    await sendInvitationMail(account.email, 'E-posta değişikliği istendi', 'Panel hesabınız için e-posta değişikliği istendi. Yeni adres doğrulanmadan mevcut adresiniz değişmez.')
    return { success: true, message: 'Yeni adresinize doğrulama bağlantısı gönderildi. E-posta adresiniz doğrulama tamamlandıktan sonra değişir.' }
  } catch {
    return { error: 'Doğrulama e-postası gönderilemedi. Adres değişimi tamamlanmadı; daha sonra yeniden deneyin.' }
  }
}

export async function setInvitationPassword(newPassword: string, confirmation: string) {
  const supabase = await createClient()
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error || !user?.email_confirmed_at || user.app_metadata?.gs29_password_pending !== true) {
    return { error: 'Parola belirlemek için davet bağlantısını doğrulayın.' }
  }
  const passwordError = passwordPolicyError(newPassword)
  if (passwordError) return { error: passwordError }
  if (newPassword !== confirmation) return { error: 'Şifreler eşleşmiyor.' }
  const { error: updateError } = await supabase.auth.updateUser({ password: newPassword })
  if (updateError) return { error: 'Şifre kaydedilemedi. Yeniden deneyin.' }
  // İstemci user_metadata'sı bu yetkiyi açamaz. Yalnız yeni davette service
  // tarafından konan app_metadata bayrağı, aynı Auth UID için kaldırılır.
  const { error: flagError } = await createServiceClient().auth.admin.updateUserById(user.id, { app_metadata: { ...user.app_metadata, gs29_password_pending: false } })
  if (flagError) return { error: 'Şifre kaydedildi fakat davet tamamlanamadı. Yeniden deneyin.' }
  return { success: true, message: 'Şifreniz kaydedildi. Panele giriş yapabilirsiniz.' }
}

export async function updatePassword(currentPassword: string, newPassword: string) {
  const supabase = await createClient()
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return { error: 'Oturum bulunamadı' }
  if (!(await checkAal(supabase, 'optional')).ok) return { error: 'İki faktörlü doğrulama gerekli.' }

  const passwordError = passwordPolicyError(newPassword)
  if (passwordError) return { error: passwordError }

  // Mevcut şifreyi doğrula
  if (!user.email || !(await verifyPassword(user.id, user.email, currentPassword))) {
    return { error: 'Mevcut şifre hatalı.' }
  }

  const { error } = await supabase.auth.updateUser({ password: newPassword })
  if (error) return { error: error.message }

  return { success: true, message: 'Şifreniz başarıyla güncellendi.' }
}

async function verifyPassword(userId: string, email: string, password: string): Promise<boolean> {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL, key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
  if (!url || !key || !password) return false
  const client = createPasswordClient(url, key, { auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false } })
  try {
    const { data, error } = await client.auth.signInWithPassword({ email, password })
    return !error && data.user?.id === userId
  } catch { return false } finally { await client.auth.signOut({ scope: 'local' }).catch(() => undefined) }
}
